/* eslint-disable react-hooks/exhaustive-deps */
import { RefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { IconPlay } from "@/components/icons";
import { IconPause } from "@/components/icons/player.icons";

import AdvertisementRequest from "@/api/advertisement/advertisement.requests";
import { AdTimeCode, AdvDetail } from "@/api/advertisement/advertisement.types";

import { usePlayerControls } from "../hooks/usePlayerControls";

type AdvertisementProps = {
  videoRef: RefObject<HTMLVideoElement>;
  advTimeCodes: AdTimeCode[];
};

const PlayerAdvertisement = ({ videoRef, advTimeCodes }: AdvertisementProps) => {
  const adRef = useRef<HTMLVideoElement>(null);

  const { togglePlay, isPlaying } = usePlayerControls(videoRef);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentData, setCurrentData] = useState<AdvDetail | null>(null);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlayingAd, setIsPlayingAd] = useState(true);
  const [t] = useTranslation();

  useEffect(() => {
    if (isPlaying && advTimeCodes) togglePlay();
  }, []);

  const fetchDetail = async (id: number) => {
    try {
      const data = await AdvertisementRequest.getAdvDetail(id);
      if (data) {
        setCurrentData(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (advTimeCodes && advTimeCodes[currentIndex]) {
      fetchDetail(advTimeCodes[currentIndex].id);
    }
  }, [currentIndex, advTimeCodes]);

  const handleEnded = () => {
    if (!advTimeCodes) return;

    const nextIndex = currentIndex + 1;

    if (nextIndex < advTimeCodes.length) {
      setCurrentIndex(nextIndex);
    } else {
      setCurrentData(null);
      togglePlay();
    }
  };

  useEffect(() => {
    const blockKeys = (e: KeyboardEvent) => {
      const keys = [" ", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
      if (keys.includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    if (currentData) {
      window.addEventListener("keydown", blockKeys, true);
      document.addEventListener("keydown", blockKeys, true);
    }

    return () => {
      window.removeEventListener("keydown", blockKeys, true);
      document.removeEventListener("keydown", blockKeys, true);
    };
  }, [currentData]);

  const togglePlayAd = () => {
    if (!adRef.current) return;
    if (adRef.current.paused) {
      adRef.current.play();
      // setStatistic(AdAction.play);
      setIsPlayingAd(true);
    } else {
      adRef.current.pause();
      // setStatistic(AdAction.pause);
      setIsPlayingAd(false);
    }
  };

  return currentData ? (
    <div className="absolute left-0 top-0 z-[100000] h-full w-full bg-black">
      <video
        playsInline
        ref={adRef}
        tabIndex={-1}
        className="h-full w-full object-contain"
        src={currentData.video}
        autoPlay
        controls={false}
        onPlay={() => isPlaying && togglePlay()}
        onEnded={handleEnded}
        onTimeUpdate={(e) => {
          const { currentTime, duration } = e.currentTarget;
          setCurrentTime(currentTime);
          setProgress((currentTime / duration) * 100);
        }}
      />

      <div className="absolute left-0 top-0 flex h-full w-full flex-col justify-between p-10 max-lg:p-4">
        <div className="flex items-center justify-between">
          {/* <IconX onClick={() => navigate(-1)} className="player-icon stroke-white" /> */}
          <div></div>
          <p className="text-[18px]">
            {t("player.advertisement")}{" "}
            {advTimeCodes && advTimeCodes.findIndex((item) => item.id === currentData?.id) + 1}/{advTimeCodes?.length}
          </p>
        </div>

        <Link to={currentData.link} target="_blank" className="h-full w-full" />

        <div className="flex items-center justify-between">
          <div onClick={togglePlayAd}>
            {isPlayingAd ? <IconPause className="player-icon" /> : <IconPlay className="player-icon" />}
          </div>
          <div className="flex items-center justify-end gap-2">
            <div className="relative h-6 w-6">
              <svg className="h-full w-full rotate-[-90deg]" viewBox="0 0 36 36">
                <circle
                  className="text-gray-500"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="transparent"
                  r="16"
                  cx="18"
                  cy="18"
                />
                <circle
                  className="text-orange-1"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeLinecap="round"
                  fill="transparent"
                  r="16"
                  cx="18"
                  cy="18"
                  strokeDasharray="100"
                  strokeDashoffset={100 - progress}
                />
              </svg>
            </div>
            <button
              onClick={() => {
                if ((currentData?.skip_time || 0) <= currentTime) {
                  const nextIndex = currentIndex + 1;
                  if (nextIndex < (advTimeCodes?.length || 0)) {
                    setCurrentIndex(nextIndex);
                  } else {
                    setCurrentData(null);
                    togglePlay();
                  }
                }
              }}
              disabled={(currentData?.skip_time || 0) > currentTime}
              className={`text-[18px] transition-opacity duration-300 ${
                (currentData?.skip_time || 0) > currentTime ? "cursor-not-allowed opacity-50" : "opacity-100"
              }`}
            >
              {(currentData?.skip_time || 0) > currentTime
                ? `${t("player.skip-after")} ${Math.ceil((currentData?.skip_time || 0) - currentTime)}`
                : t("player.skip")}
            </button>
          </div>
        </div>
      </div>
    </div>
  ) : null;
};

export default PlayerAdvertisement;
